import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

/**
 * O que a página mostra na barra superior: só o nome dela.
 *
 * A barra tem dono fixo — switcher de organização no centro, sino e perfil à direita, e o atalho de
 * atendimento enquanto houver um em curso. Botão de página mora na própria página.
 *
 * Por isso não existe mais `actions`, e `title` é texto e não componente: antes a barra aceitava
 * qualquer coisa, e cada página foi pondo seus botões lá — a Agenda chegou a pôr a barra inteira
 * de controles no lugar do título. Com o tipo fechado, voltar a fazer isso deixa de compilar.
 */
type PageHeader = {
  title: string;
  subtitle?: string;
};

type Ctx = {
  header: PageHeader | null;
  setHeader: (h: PageHeader | null) => void;
};

const PageHeaderContext = createContext<Ctx | null>(null);

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [header, setHeader] = useState<PageHeader | null>(null);
  const value = useMemo(() => ({ header, setHeader }), [header]);
  return <PageHeaderContext.Provider value={value}>{children}</PageHeaderContext.Provider>;
}

export function usePageHeader() {
  const ctx = useContext(PageHeaderContext);
  if (!ctx) throw new Error("usePageHeader fora do PageHeaderProvider");
  return ctx.header;
}

/** Registra título e subtítulo no Topbar. Limpa ao desmontar. `ativo: false` = a página está
 *  embutida em outra (uma seção de Configurações), e quem manda no título é a de fora. */
export function useSetPageHeader(header: PageHeader | null, deps: unknown[] = [], ativo = true) {
  const ctx = useContext(PageHeaderContext);
  if (!ctx) throw new Error("useSetPageHeader fora do PageHeaderProvider");

  useEffect(() => {
    if (!ativo) return;
    ctx.setHeader(header);
    return () => ctx.setHeader(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, ativo]);
}
